// ============================================================================
// Ciclo de vida: transiciones de estado, costos visibles y resumen
// ============================================================================
// Reglas del MVP (sección 1 del diseño):
//   · asignar (persona, unidad y/o ubicación) → `en_uso`;
//   · devolver al almacén → `en_almacen`, sin asignación;
//   · abrir mantenimiento → `en_mantenimiento`; cerrarlo vuelve al estado que
//     tenía al abrir (`statusBefore`);
//   · `baja` es final: no se borra, no se reasigna y deja de depreciarse.
// Mientras hay un mantenimiento abierto no se asigna, no se devuelve ni se da
// de baja: primero se cierra.
// ============================================================================

import type { Asset, AssetAssignment, AssetMaintenance, AssetPurchase } from "./types";
import { computeBookValue, round2 } from "./depreciation";
import type { AssetCategory, AssetStatus } from "./vocab";
import { ASSET_CATEGORIES, ASSET_STATUSES, LIVE_ASSET_STATUSES, isAssetStatus } from "./vocab";

export const ASSET_ACTIONS = [
  "editar",
  "asignar",
  "devolver",
  "abrir_mantenimiento",
  "cerrar_mantenimiento",
  "baja",
] as const;
export type AssetAction = (typeof ASSET_ACTIONS)[number];

/** Desde qué estados se puede hacer cada acción. */
const ALLOWED_FROM: Record<AssetAction, readonly AssetStatus[]> = {
  editar: ["en_almacen", "en_uso", "en_mantenimiento"],
  asignar: ["en_almacen", "en_uso"],
  devolver: ["en_uso"],
  abrir_mantenimiento: ["en_almacen", "en_uso"],
  cerrar_mantenimiento: ["en_mantenimiento"],
  baja: ["en_almacen", "en_uso"],
};

export function canApplyAction(status: AssetStatus, action: AssetAction): boolean {
  return ALLOWED_FROM[action].includes(status);
}

export type TransitionResult =
  | { ok: true; status: AssetStatus }
  | { ok: false; code: "ESTADO_INVALIDO"; from: AssetStatus; action: AssetAction };

/**
 * Estado que resulta de aplicar `action`. Para cerrar un mantenimiento pasa
 * `statusBefore` (el que guardó el mantenimiento al abrir); si falta o no es
 * válido, el activo vuelve a `en_almacen`.
 */
export function nextStatus(
  status: AssetStatus,
  action: AssetAction,
  opts: { statusBefore?: AssetStatus | null } = {},
): TransitionResult {
  if (!canApplyAction(status, action)) return { ok: false, code: "ESTADO_INVALIDO", from: status, action };
  switch (action) {
    case "editar":
      return { ok: true, status };
    case "asignar":
      return { ok: true, status: "en_uso" };
    case "devolver":
      return { ok: true, status: "en_almacen" };
    case "abrir_mantenimiento":
      return { ok: true, status: "en_mantenimiento" };
    case "cerrar_mantenimiento": {
      const back = opts.statusBefore;
      const valid = isAssetStatus(back) && back !== "en_mantenimiento" && back !== "baja";
      return { ok: true, status: valid ? back : "en_almacen" };
    }
    case "baja":
      return { ok: true, status: "baja" };
  }
}

/** Asignación vacía (activo en almacén). */
export function emptyAssignment(since: string | null = null): AssetAssignment {
  return { custodian: null, vehicleId: null, baseId: null, location: null, since };
}

/** La asignación tiene al menos persona, unidad, base o ubicación. */
export function hasAssignment(a: Partial<AssetAssignment> | null | undefined): boolean {
  if (!a) return false;
  return Boolean(a.custodian?.id || a.vehicleId || a.baseId || (a.location && a.location.trim()));
}

// --- Costos visibles (D6: `feat:activos.costos`) ----------------------------------

/**
 * Quita los montos de un activo para quien no tiene `feat:activos.costos`.
 * Se conservan proveedor, fecha, moneda y factura; no se toca el original.
 */
export function stripAssetCosts<T extends Pick<Asset, "purchase" | "depreciation">>(asset: T): T {
  return {
    ...asset,
    purchase: { ...asset.purchase, unitCost: null, exchangeRate: null, costBase: null },
    depreciation: { ...asset.depreciation, salvage: 0 },
  };
}

/** Quita los montos y el estado de finanzas de una compra. */
export function stripPurchaseCosts<T extends Pick<AssetPurchase, "exchangeRate" | "subtotal" | "tax" | "total" | "totalBase" | "finance">>(
  purchase: T,
): Omit<T, "finance"> & { finance: null } {
  return { ...purchase, exchangeRate: null, subtotal: null, tax: null, total: null, totalBase: null, finance: null };
}

/** Quita los montos y el estado de finanzas de un mantenimiento. */
export function stripMaintenanceCosts<T extends Pick<AssetMaintenance, "cost" | "exchangeRate" | "costBase" | "finance">>(
  m: T,
): Omit<T, "finance"> & { finance: null } {
  return { ...m, cost: null, exchangeRate: null, costBase: null, finance: null };
}

// --- Resumen del inventario (`/assetsSummary`) ----------------------------------------

export interface AssetsSummary {
  total: number;
  byStatus: Record<AssetStatus, number>;
  /** Solo inventario vivo (sin bajas). */
  byCategory: Record<AssetCategory, number>;
  /** Costo de compra en MXN del inventario vivo; null sin `feat:activos.costos`. */
  totalBase: number | null;
  /** Valor en libros al `asOf` del inventario vivo; null sin `feat:activos.costos`. */
  bookValueBase: number | null;
  /** Activos vivos sin costo en MXN (no suman a los totales). */
  withoutCost: number;
}

const zeros = <K extends string>(keys: readonly K[]) =>
  Object.fromEntries(keys.map((k) => [k, 0])) as Record<K, number>;

export function summarizeAssets(
  assets: ReadonlyArray<Pick<Asset, "status" | "category" | "purchase" | "depreciation" | "retiredAt">>,
  opts: { canSeeCosts: boolean; asOf: string; usefulLifeByCategory?: Partial<Record<AssetCategory, number>> },
): AssetsSummary {
  const byStatus = zeros(ASSET_STATUSES);
  const byCategory = zeros(ASSET_CATEGORIES);
  let totalBase = 0;
  let bookValueBase = 0;
  let withoutCost = 0;
  for (const a of assets) {
    if (isAssetStatus(a.status)) byStatus[a.status] += 1;
    if (!LIVE_ASSET_STATUSES.includes(a.status)) continue;
    if (a.category in byCategory) byCategory[a.category] += 1;
    const bv = computeBookValue(a, opts.asOf, opts.usefulLifeByCategory);
    if (!bv) {
      withoutCost += 1;
      continue;
    }
    totalBase += bv.cost;
    bookValueBase += bv.bookValue;
  }
  return {
    total: assets.length,
    byStatus,
    byCategory,
    totalBase: opts.canSeeCosts ? round2(totalBase) : null,
    bookValueBase: opts.canSeeCosts ? round2(bookValueBase) : null,
    withoutCost: opts.canSeeCosts ? withoutCost : 0,
  };
}
